import { useCallback, useEffect, useState } from "react";
import { Bot, MessageSquare, Mic, MicOff, Volume2 } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useDialysisAssistant } from "../hooks/useDialysisAssistant";
import { useAssistantVoice } from "../hooks/useAssistantVoice";
import AssistantPendingLogAction from "../components/AssistantPendingLogAction";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

export default function DialysisAssistant() {
  const { t } = useLang();
  const {
    question,
    setQuestion,
    answer,
    error,
    loading,
    usageCount,
    dailyLimit,
    remaining,
    askAssistant,
    pendingLogAction,
    confirmPendingLogAction,
    cancelPendingLogAction,
  } = useDialysisAssistant();
  const [voiceQuestionToAsk, setVoiceQuestionToAsk] = useState("");
  const handleTranscript = useCallback(
    (transcript) => {
      const voiceQuestion = transcript.trim();
      setQuestion(voiceQuestion);
      if (voiceQuestion && !loading && remaining > 0) setVoiceQuestionToAsk(voiceQuestion);
    },
    [loading, remaining, setQuestion]
  );
  const {
    listening,
    recording,
    transcribing,
    speaking,
    speechLoading,
    voiceError,
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    clearVoiceError,
  } = useAssistantVoice({ onTranscript: handleTranscript });

  useEffect(() => {
    if (!voiceQuestionToAsk) return;
    if (loading || remaining === 0) {
      setVoiceQuestionToAsk("");
      return;
    }

    const askAndSpeak = async () => {
      const voiceQuestion = voiceQuestionToAsk;
      setVoiceQuestionToAsk("");
      const result = await askAssistant(voiceQuestion);
      if (result?.ok) {
        await speak(result.answer, { silentError: true });
      }
    };

    askAndSpeak();
  }, [askAssistant, loading, remaining, speak, voiceQuestionToAsk]);

  const handleTypedChange = (event) => {
    clearVoiceError();
    setQuestion(event.target.value);
  };

  const handleManualAsk = () => {
    clearVoiceError();
    askAssistant();
  };

  const handleMicStart = (event) => {
    event.preventDefault();
    startListening();
  };

  const handleMicStop = (event) => {
    event.preventDefault();
    if (recording) stopListening();
  };

  const voiceStatus = voiceError || (transcribing
    ? t("assistant_voice_transcribing")
    : recording
      ? t("assistant_voice_recording")
      : speechLoading
        ? t("assistant_speech_loading")
        : speaking
          ? t("assistant_voice_speaking")
          : "");

  return (
    <div className="space-y-6 max-w-3xl" data-testid="assistant-page">
      <div>
        <div className="flex items-center gap-3">
          <Bot className="w-8 h-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
            {t("assistant_title")}
          </h1>
        </div>
        <p className="text-muted-foreground mt-2">{t("assistant_intro")}</p>
      </div>

      <div className="bento-card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="assistant-question">{t("assistant_question_label")}</Label>
          <span className="text-xs text-muted-foreground">
            {usageCount} / {dailyLimit} {t("assistant_usage")} | {remaining} {t("assistant_remaining")}
          </span>
        </div>
        <Textarea
          id="assistant-question"
          value={question}
          onChange={handleTypedChange}
          placeholder={t("assistant_placeholder")}
          maxLength={1000}
          rows={5}
          className="resize-y"
          disabled={loading}
          data-testid="assistant-question"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className={`h-11 min-w-[9rem] justify-center px-4 whitespace-nowrap ${recording ? "border-primary bg-primary/10 text-primary" : ""}`}
            onPointerDown={handleMicStart}
            onPointerUp={handleMicStop}
            onPointerCancel={handleMicStop}
            onPointerLeave={handleMicStop}
            onKeyDown={(event) => {
              if ((event.key === " " || event.key === "Enter") && !recording) handleMicStart(event);
            }}
            onKeyUp={(event) => {
              if (event.key === " " || event.key === "Enter") handleMicStop(event);
            }}
            disabled={loading || transcribing}
            data-testid="assistant-mic"
            aria-label={recording ? t("assistant_voice_stop") : t("assistant_voice_start")}
          >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            {recording ? t("assistant_voice_recording") : t("assistant_voice_start")}
          </Button>
          <Button
            className="btn-primary h-11 px-6"
            onClick={handleManualAsk}
            disabled={loading || !question.trim() || remaining === 0}
            data-testid="assistant-submit"
          >
            <MessageSquare className="w-4 h-4" />
            {loading ? t("assistant_thinking") : t("assistant_ask")}
          </Button>
        </div>
        <p
          className={`min-h-5 text-xs ${voiceError ? "text-destructive" : "text-muted-foreground"}`}
          data-testid={voiceError ? "assistant-voice-error" : "assistant-listening"}
        >
          {voiceStatus || "\u00a0"}
        </p>
        {remaining === 0 && (
          <p className="text-sm text-destructive">{t("assistant_limit_reached")}</p>
        )}
        <p className="text-xs text-muted-foreground">{t("assistant_disclaimer")}</p>
      </div>

      {(answer || error) && (
        <div className="bento-card" aria-live="polite">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-lg">{t("assistant_response_title")}</h2>
            {answer && !error && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={speaking ? stopSpeaking : () => speak(answer)}
                data-testid="assistant-speak"
                aria-label={speaking ? t("assistant_stop_speaking") : t("assistant_speak")}
              >
                <Volume2 className="w-4 h-4" />
                {speechLoading ? t("assistant_speech_loading") : speaking ? t("assistant_voice_speaking") : t("assistant_speak")}
              </Button>
            )}
          </div>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <p className="text-sm md:text-base whitespace-pre-wrap leading-relaxed">{answer}</p>
          )}
          <div className="mt-3">
            <AssistantPendingLogAction
              action={pendingLogAction}
              onConfirm={confirmPendingLogAction}
              onCancel={cancelPendingLogAction}
              t={t}
            />
          </div>
        </div>
      )}
    </div>
  );
}
